import { Button, Checkbox } from "src/elements";
import { useLoaders } from "../InlineLoader";
import { useApi } from "src/api";
import {
  PayloadPostMerchflows_merchflowId_ExportsBatch,
  ResponsePostMerchflows_merchflowId_ExportsBatch,
  RoutePostMerchflows_merchflowId_ExportsBatch,
} from "@CommonApi/merchflows/_merchflowId_/exports/batch";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "src/i18n";
import { sleep } from "src/utils/sleep";
import { usePogCsvColumnsModal } from "src/modals/PogCsvColumns/store/hooks";

interface Props {
  merchflowId: number;
  storePogsIds: number[];
  isDisabled?: boolean;
  isAllSelected?: boolean;
  /** True when the user is looking at a version other than the latest. */
  isNonLatestVersionSelected?: boolean;
  areAllStoreActionsDisabled?: boolean;
  disabledTooltip?: string;
  onPdfExportStatusChange?: () => void;
  onPdfExportBatchChange?: (batch?: ResponsePostMerchflows_merchflowId_ExportsBatch) => void;
}

export const StorePogsExporter = ({
  merchflowId,
  storePogsIds,
  isDisabled,
  isAllSelected,
  isNonLatestVersionSelected = false,
  areAllStoreActionsDisabled = false,
  disabledTooltip,
  onPdfExportStatusChange,
  onPdfExportBatchChange,
}: Props) => {
  const { hasLoader } = useLoaders();
  const { postExportBatchPogsApi } = useApi();
  const { openPogCsvColumnsModal } = usePogCsvColumnsModal();
  const { t } = useTranslation("common");

  const [isSuccessful, setIsSuccessful] = useState(false);
  const [forceOverwrite, setForceOverwrite] = useState(false);
  const [isHoveringNonPdfExport, setIsHoveringNonPdfExport] = useState(false);
  const isMounted = useRef(true);
  const activeMerchflowId = useRef(merchflowId);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    activeMerchflowId.current = merchflowId;
  }, [merchflowId]);

  const isLoading = hasLoader(RoutePostMerchflows_merchflowId_ExportsBatch);

  const showSuccess = async () => {
    if (!isMounted.current) return;
    setIsSuccessful(true);
    await sleep(1000);
    if (isMounted.current) setIsSuccessful(false);
  };

  const exportStorePogs = async (payload: PayloadPostMerchflows_merchflowId_ExportsBatch) => {
    if (payload.type === "pdfV2") {
      onPdfExportBatchChange?.(undefined);
    }
    const isPdfPollingActive = () => isMounted.current && activeMerchflowId.current === merchflowId;
    const onBatchStatusChange =
      payload.type === "pdfV2"
        ? (batch: ResponsePostMerchflows_merchflowId_ExportsBatch) => {
            if (isPdfPollingActive()) {
              onPdfExportBatchChange?.(batch);
              onPdfExportStatusChange?.();
            }
          }
        : undefined;
    const data = await postExportBatchPogsApi(
      merchflowId,
      payload,
      onBatchStatusChange,
      payload.type === "pdfV2" ? isPdfPollingActive : undefined,
    );

    if (payload.type !== "pdfV2" && data?.url) await showSuccess();
  };

  function getSelectedPayload<T extends "pdfV2" | "csv" | "psaV2">(type: T) {
    const payload = isAllSelected
      ? { type, exportAllStorePogExclude: storePogsIds }
      : { type, store_pogs_ids: storePogsIds };
    return type === "pdfV2" ? { ...payload, overwrite: forceOverwrite } : payload;
  }

  return (
    <Button
      isLoading={isLoading}
      isSuccessful={isSuccessful}
      isDisabled={isDisabled}
      dropdown={[
        {
          label: "Export selected",
          isDisabled: !isAllSelected && storePogsIds.length === 0,
          // Only when selecting all: individually ticked rows carry their own
          // version's ids, so those export exactly what is on screen.
          tooltip:
            isAllSelected && isNonLatestVersionSelected
              ? t("storePogExport.latestVersionNote")
              : undefined,
          buttons: [
            {
              label: "PDF",
              onClick: () => exportStorePogs(getSelectedPayload("pdfV2")),
            },
            {
              label: "CSV",
              onClick: () =>
                openPogCsvColumnsModal({
                  merchflowId,
                  payload: getSelectedPayload("csv"),
                  onSuccess: () => showSuccess(),
                }),
              onMouseEnter: () => setIsHoveringNonPdfExport(true),
              onMouseLeave: () => setIsHoveringNonPdfExport(false),
            },
            {
              label: "PSA",
              onClick: () => exportStorePogs(getSelectedPayload("psaV2")),
              onMouseEnter: () => setIsHoveringNonPdfExport(true),
              onMouseLeave: () => setIsHoveringNonPdfExport(false),
            },
          ],
        },
        {
          label: "Export approved",
          isDisabled: areAllStoreActionsDisabled,
          tooltip: areAllStoreActionsDisabled
            ? (disabledTooltip ??
              "Clear the store code search to export across all approved stores.")
            : t("storePogExport.approvedVersionNote"),
          buttons: [
            {
              label: "PDF",
              onClick: () =>
                exportStorePogs({
                  type: "pdfV2",
                  approved_store_pogs: true,
                  overwrite: forceOverwrite,
                }),
            },
            {
              label: "CSV",
              onClick: () =>
                openPogCsvColumnsModal({
                  merchflowId,
                  payload: { type: "csv", approved_store_pogs: true },
                  onSuccess: () => showSuccess(),
                }),
              onMouseEnter: () => setIsHoveringNonPdfExport(true),
              onMouseLeave: () => setIsHoveringNonPdfExport(false),
            },
            {
              label: "PSA",
              onClick: () => exportStorePogs({ type: "psaV2", approved_store_pogs: true }),
              onMouseEnter: () => setIsHoveringNonPdfExport(true),
              onMouseLeave: () => setIsHoveringNonPdfExport(false),
            },
          ],
        },
        {
          label: "",
          content: (
            <Checkbox
              name="forceOverwriteExport"
              label={t("storePogExport.forceOverwritePdfs")}
              isChecked={forceOverwrite}
              setIsChecked={setForceOverwrite}
              isDisabled={isHoveringNonPdfExport}
              size="small"
              role="menuitemcheckbox"
            />
          ),
        },
      ]}
    >
      Exports
    </Button>
  );
};
